import { ChartSchema } from '../../../types/types';

/**
 * Type definition for scale functions that handle both string and number inputs
 */
export type ScaleFunction = (value: string | number) => number;

/**
 * Calculate min and max values for Y axis
 */
export const calculateYMinMax = (series: ChartSchema['series'], chartType?: string) => {
  let min = Infinity;
  let max = -Infinity;

  // Handle case where series is undefined
  if (!series || series.length === 0) {
    return { min: 0, max: 0 };
  }

  series.forEach(seriesItem => {
    if (seriesItem.data) {
      seriesItem.data.forEach(point => {
        if (point.y < min) min = point.y;
        if (point.y > max) max = point.y;
      });
    }
  });

  // Add some padding to the min/max values
  const padding = (max - min) * 0.1;

  // For bar charts, always start from 0 (unless we have negative values)
  if (chartType === 'bar' || chartType === 'horizontal-bar') {
    min = min < 0 ? min - padding : 0;
  } else {
    min = Math.max(0, min - padding); // Don't go below 0 for financial charts
  }

  max = max + padding;

  return { min, max };
};

/**
 * Get X values based on array indexes to handle duplicate labels
 * Uses the first series as the reference for X-axis positions
 * Returns indexes as strings to maintain consistent positioning across all series
 */
export const getUniqueXValues = (series: ChartSchema['series'], reverse: boolean = true) => {
  // Handle case where series is undefined
  if (!series || series.length === 0) {
    return [];
  }

  // Use the first series to determine the number of data points
  const firstSeries = series[0];
  if (!firstSeries?.data || firstSeries.data.length === 0) {
    return [];
  }

  // Create array of indexes as strings (0, 1, 2, 3, etc.)
  const indexValues = firstSeries.data.map((_, index) => index.toString());

  // Reverse the order if specified
  return reverse ? indexValues.reverse() : indexValues;
};

/**
 * Create X scale function
 */
export const createXScale = (
  xValues: (string | number)[],
  chartWidth: number,
  margin: { left: number; right: number }
): ScaleFunction => {
  // Handle the case where there's only one value
  if (xValues.length === 1) {
    return (value: string | number): number => margin.left + chartWidth / 2;
  }

  // Maximum column width in pixels
  const MAX_COLUMN_WIDTH = 60;

  // Calculate the optimal band width based on available space
  // Use a smaller padding ratio for more columns to maximize space usage
  const paddingRatio = Math.max(0.05, Math.min(0.15, 0.2 - xValues.length * 0.01));

  // Calculate the band width - the space allocated for each data point
  const rawBandWidth = chartWidth / xValues.length;

  // Limit the band width to ensure columns aren't too wide
  // Add extra padding when columns would be too wide
  const bandWidth = Math.min(rawBandWidth, MAX_COLUMN_WIDTH + paddingRatio * 2 * MAX_COLUMN_WIDTH);

  // Calculate the actual width of each column (band width minus padding)
  const columnWidth = Math.min(bandWidth * (1 - paddingRatio * 2), MAX_COLUMN_WIDTH);

  // If we need to limit column width, recalculate the total width used
  const totalWidthNeeded = bandWidth * xValues.length;

  // Calculate the step between column centers
  const step = chartWidth / xValues.length;

  // Calculate the starting offset to center the columns if they don't use full width
  const startOffset = totalWidthNeeded < chartWidth ? (chartWidth - totalWidthNeeded) / 2 : 0;

  return (value: string | number): number => {
    const index = xValues.indexOf(value);
    if (index === -1) return margin.left; // Default if value not found

    // If we have only 2 values, place them at 1/4 and 3/4 of the width for better spacing
    if (xValues.length === 2) {
      return margin.left + chartWidth * (0.25 + index * 0.5);
    }

    // If we have 3-5 values, use wider columns but respect max width
    if (xValues.length <= 5) {
      // Calculate positions with max width constraint and proper centering
      const effectiveWidth = Math.min(chartWidth, bandWidth * xValues.length);
      const effectivePadding = (chartWidth - effectiveWidth) / 2;

      // Position each column with equal spacing, respecting the max width
      return margin.left + effectivePadding + index * bandWidth + bandWidth / 2;
    }

    // For more values, distribute evenly with adaptive padding
    // This centers each column in its allocated space
    return margin.left + startOffset + index * bandWidth + bandWidth / 2;
  };
};

/**
 * Create Y scale function
 */
export const createYScale = (
  yMinMax: { min: number; max: number },
  height: number,
  chartHeight: number,
  margin: { top: number; bottom: number }
): ScaleFunction => {
  return (value: string | number): number => {
    // Convert string values to numbers if needed
    const numValue = typeof value === 'string' ? Number(value) : value;
    return height - margin.bottom - ((numValue - yMinMax.min) / (yMinMax.max - yMinMax.min)) * chartHeight;
  };
};

/**
 * Generate Y-axis ticks
 */
export const generateYTicks = (yMinMax: { min: number; max: number }, tickCount = 5) => {
  const step = (yMinMax.max - yMinMax.min) / (tickCount - 1);
  return Array.from({ length: tickCount }, (_, i) => yMinMax.min + step * i);
};
